import React from 'react';
import PropTypes from 'prop-types';

import { App } from '@meronex/app';
import { CloseBtn, toast, Loading } from '@meronex/components';

import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import CircularProgress from '@material-ui/core/CircularProgress';

import IStores from '@meronex/icons/fi/FiPackage';
import ISearch from '@meronex/icons/fi/FiSearch';
import IAdd from '@meronex/icons/fi/FiPlus';
import IArchive from '@meronex/icons/fi/FiArchive';
import IEdit from '@meronex/icons/fi/FiEdit2';
import IAudit from '@meronex/icons/fi/FiClipboard';
import ICheck from '@meronex/icons/fi/FiCheck';
import IMore from '@meronex/icons/fi/FiMoreVertical';

import { useTheme } from '@material-ui/core/styles';

import AddStoreDialog from './AddStoreDialog';
import EditStoreDialog from './EditStoreDialog';

import { storesMng } from '../../api/storesMng';
import ArchivedStoreDialog from './ArchivedStoresDialog';
import AuditStoreDialog from './AuditStoreDialog';
import StoreArchivesWarningsDialog from './StoreArchivesWarningsDialog';
import { Icons } from '../../../common/Icons';

import { storeCategoriesMng } from '../../../configurations/storeCategories/api/storeCategoriesMng';

function StoresDialog(props) {
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  const {
    open,
    onClose,
    stores,
    onStoreSelect,
    refreshStores,
    selectedStore,
    permissions,
  } = props;

  const [filterValue, setFilterValue] = React.useState('');
  const [categoryId, setCategoryId] = React.useState('any');
  const [storeCategories, setStoreCategories] = React.useState([]);

  const [openAddStoreDialog, setOpenAddStoreDialog] = React.useState(false);
  const [openEditStoreDialog, setOpenEditStoreDialog] = React.useState(false);
  const [openArchivedStoresDialog, setOpenArchivedStoresDialog] = React.useState(false);
  const [archiveWarnings, setArchiveWarnings] = React.useState();
  const [openAuditDialog, setOpenAuditDialog] = React.useState(false);

  const [archiving, setArchiving] = React.useState(false);
  const [archivedStoresCount, setArchivedStoresCount] = React.useState(0);
  const [openWarningsDialog, setOpenWarningsDialog] = React.useState();
  const [switchingStore, setSwitchingStore] = React.useState(false);
  const [contextMenu, setContextMenu] = React.useState(null);

  const searchRef = React.useRef(null);

  const fetchArchivedStoresCount = async () => {
    const count = await storesMng.getArchivedStoresCount();
    setArchivedStoresCount(count);
    return count;
  };

  React.useEffect(() => {
    if (open) {
      fetchArchivedStoresCount();
      storeCategoriesMng.getStoreCategories().then((result) => {
        setStoreCategories(Array.isArray(result) ? result : []);
      });
      const savedCategoryId = App.clientSettings.getItem('storeCategoryId');
      if (savedCategoryId) setCategoryId(savedCategoryId);
    }
  }, [open]);

  React.useEffect(() => {
    fetchArchivedStoresCount();
  }, [stores]);

  // Close context menu on any click
  React.useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [contextMenu]);

  const onEditClick = (store) => {
    onStoreSelect(store);
    setOpenEditStoreDialog(true);
  };

  const onAuditClick = (store) => {
    onStoreSelect(store);
    setOpenAuditDialog(true);
  };

  const _refresh = () => {
    if (Array.isArray(stores) && stores.length > 0) {
      refreshStores((updatedStores) => {
        onStoreSelect(updatedStores[0]);
        fetchArchivedStoresCount();
      });
    }
  };

  const archiveStore = async (store) => {
    if (stores.length <= 1) {
      toast.error('You need at least one active store.', { autoClose: 900 });
    } else if (!archiving) {
      setArchiving(true);
      try {
        await storesMng.archiveStore(store._id);
        _refresh();
        toast.success('Store archived successfully', { autoClose: 900 });
      } catch (e) {
        if (
          e.error === 'archiveStore.destinationStore' ||
          e.error === 'archiveStore.productStoreItems'
        ) {
          setOpenWarningsDialog(true);
          setArchiveWarnings(e);
        } else {
          toast.error('Ops, something went wrong');
          console.error(e);
        }
      } finally {
        setArchiving(false);
      }
    }
  };

  const getFilteredStores = () => {
    let _stores = stores;
    if (filterValue) {
      _stores = _stores.filter((s) =>
        s.name.toLowerCase().includes(filterValue.toLowerCase())
      );
    }
    if (categoryId && categoryId !== 'any') {
      _stores = _stores.filter((s) => s.category && s.category._id === categoryId);
    }
    return _stores;
  };

  const _onClose = () => {
    onClose();
    setTimeout(() => {
      setFilterValue('');
    }, 500);
  };

  const onCategoryChange = (id) => {
    setCategoryId(id);
    App.clientSettings.setItem('storeCategoryId', id);
  };

  const _stores = getFilteredStores();

  const mutedColor = isDark ? '#94a3b8' : '#64748b';
  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0';
  const cardBg = isDark ? 'rgba(255,255,255,0.03)' : '#fff';
  const hoverBg = isDark ? 'rgba(255,255,255,0.06)' : '#f8fafc';

  const renderStoreCard = (store) => {
    const isSelected = selectedStore && selectedStore._id === store._id;
    const isArchiving = archiving && contextMenu?.store?._id === store._id;

    return (
      <div
        key={store._id}
        onClick={() => {
          if (isArchiving) return;
          setSwitchingStore(true);
          onStoreSelect(store, () => {
            setSwitchingStore(false);
            onClose();
          });
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          padding: '14px 16px',
          borderRadius: '12px',
          cursor: 'pointer',
          backgroundColor: isSelected ? (isDark ? 'rgba(99,102,241,0.1)' : '#eef2ff') : cardBg,
          border: `1px solid ${isSelected ? (isDark ? 'rgba(99,102,241,0.3)' : '#c7d2fe') : borderColor}`,
          marginBottom: '8px',
          transition: 'all 0.15s ease',
        }}
        onMouseEnter={(e) => {
          if (!isSelected) e.currentTarget.style.backgroundColor = hoverBg;
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          if (!isSelected) e.currentTarget.style.backgroundColor = cardBg;
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        {/* Store icon/avatar */}
        <div style={{
          width: '44px',
          height: '44px',
          borderRadius: '12px',
          backgroundColor: isSelected
            ? (isDark ? 'rgba(99,102,241,0.2)' : '#e0e7ff')
            : (isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          {isSelected ? (
            <ICheck size={20} style={{ color: '#6366f1' }} />
          ) : (
            <IStores size={20} style={{ color: mutedColor }} />
          )}
        </div>

        {/* Store info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '14px',
            fontWeight: isSelected ? 600 : 500,
            color: isSelected ? '#6366f1' : (isDark ? '#e2e8f0' : '#0f172a'),
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {store.name}
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '4px',
          }}>
            <span style={{
              fontSize: '12px',
              color: mutedColor,
            }}>
              {store.itemCount || 0} item{(store.itemCount || 0) !== 1 ? 's' : ''}
            </span>
            {store.category && (
              <>
                <span style={{ fontSize: '10px', color: isDark ? '#555' : '#cbd5e1' }}>·</span>
                <span style={{
                  fontSize: '11px',
                  color: isDark ? '#666' : '#94a3b8',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '3px',
                }}>
                  <Icons.Category style={{ fontSize: '11px' }} />
                  {store.category.name}
                </span>
              </>
            )}
            {store.productsStore && (
              <>
                <span style={{ fontSize: '10px', color: isDark ? '#555' : '#cbd5e1' }}>·</span>
                <span style={{
                  fontSize: '11px',
                  color: isDark ? '#666' : '#94a3b8',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '3px',
                }}>
                  <Icons.Products style={{ fontSize: '11px' }} />
                  Products
                </span>
              </>
            )}
            {store.updatedAt && (
              <>
                <span style={{ fontSize: '10px', color: isDark ? '#555' : '#cbd5e1' }}>·</span>
                <span style={{ fontSize: '11px', color: isDark ? '#555' : '#b0b8c4' }}>
                  {App.utils.time.timeAgo(store.updatedAt.date)}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Context menu button */}
        <div
          onClick={(e) => {
            e.stopPropagation();
            const rect = e.currentTarget.getBoundingClientRect();
            setContextMenu({
              x: rect.right - 170,
              y: rect.bottom + 4,
              store,
            });
          }}
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            cursor: 'pointer',
            transition: 'background-color 0.15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          {isArchiving ? (
            <CircularProgress size={16} style={{ color: mutedColor }} />
          ) : (
            <IMore size={16} style={{ color: mutedColor }} />
          )}
        </div>
      </div>
    );
  };

  const renderContextMenu = () => {
    if (!contextMenu) return null;

    const menuItemStyle = {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '8px 14px',
      fontSize: '13px',
      cursor: 'pointer',
      color: isDark ? '#e2e8f0' : '#334155',
      whiteSpace: 'nowrap',
      transition: 'background-color 0.1s ease',
      borderRadius: '6px',
      margin: '2px 4px',
    };
    const onHover = (e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc'; };
    const onLeave = (e) => { e.currentTarget.style.backgroundColor = 'transparent'; };

    return (
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          left: Math.min(contextMenu.x, window.innerWidth - 190),
          top: Math.min(contextMenu.y, window.innerHeight - 200),
          zIndex: 9999,
          backgroundColor: isDark ? '#1e1e22' : '#fff',
          borderRadius: '12px',
          border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
          boxShadow: isDark
            ? '0 8px 24px rgba(0,0,0,0.4)'
            : '0 8px 24px rgba(0,0,0,0.12)',
          padding: '4px 0',
          minWidth: '170px',
        }}
      >
        <div style={menuItemStyle} onClick={() => {
          const store = contextMenu.store;
          setContextMenu(null);
          setSwitchingStore(true);
          onStoreSelect(store, () => {
            setSwitchingStore(false);
            onClose();
          });
        }} onMouseEnter={onHover} onMouseLeave={onLeave}>
          <ICheck size={14} style={{ opacity: 0.6 }} />
          <span>Select</span>
        </div>
        <div style={menuItemStyle} onClick={() => {
          onEditClick(contextMenu.store);
          setContextMenu(null);
        }} onMouseEnter={onHover} onMouseLeave={onLeave}>
          <IEdit size={14} style={{ opacity: 0.6 }} />
          <span>Edit</span>
        </div>
        {permissions?.auditStore && (
          <div style={menuItemStyle} onClick={() => {
            onAuditClick(contextMenu.store);
            setContextMenu(null);
          }} onMouseEnter={onHover} onMouseLeave={onLeave}>
            <IAudit size={14} style={{ opacity: 0.6 }} />
            <span>Audit</span>
          </div>
        )}
        <div style={{
          height: '1px',
          backgroundColor: isDark ? '#333' : '#f1f5f9',
          margin: '4px 8px',
        }} />
        <div
          style={{ ...menuItemStyle, color: isDark ? '#f87171' : '#dc2626' }}
          onClick={() => {
            const store = contextMenu.store;
            setContextMenu(null);
            archiveStore(store);
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.05)' : '#fef2f2'; }}
          onMouseLeave={onLeave}
        >
          <IArchive size={14} style={{ opacity: 0.6 }} />
          <span>Archive</span>
        </div>
      </div>
    );
  };

  const renderEmpty = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '48px 20px',
    }}>
      <div style={{
        width: '56px',
        height: '56px',
        borderRadius: '16px',
        backgroundColor: isDark ? 'rgba(99,102,241,0.15)' : '#eef2ff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '16px',
      }}>
        <IStores size={24} style={{ color: '#6366f1' }} />
      </div>
      <div style={{
        fontSize: '15px',
        fontWeight: 600,
        color: isDark ? '#e2e8f0' : '#0f172a',
        marginBottom: '4px',
      }}>
        {filterValue ? 'No stores match your search' : 'No stores yet'}
      </div>
      {!filterValue && (
        <div
          onClick={() => setOpenAddStoreDialog(true)}
          style={{
            fontSize: '13px',
            color: '#6366f1',
            cursor: 'pointer',
            fontWeight: 500,
            marginTop: '4px',
          }}
        >
          Create your first store
        </div>
      )}
    </div>
  );

  return (
    <Dialog
      className={'fullScreenMobile'}
      open={open}
      onClose={_onClose}
      PaperProps={{
        style: {
          borderRadius: '16px',
          maxWidth: '480px',
          width: '100%',
        },
      }}
    >
      {/* Header */}
      <div style={{
        padding: '20px 20px 0',
        position: 'relative',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}>
          <div style={{
            fontSize: '18px',
            fontWeight: 700,
            color: isDark ? '#e2e8f0' : '#0f172a',
          }}>
            Stores
          </div>
          <CloseBtn onClick={_onClose} />
        </div>

        {/* Search bar */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#f8fafc',
          border: `1px solid ${borderColor}`,
          borderRadius: '10px',
          padding: '0 12px',
          height: '40px',
          marginBottom: storeCategories.length > 0 ? '10px' : '16px',
        }}>
          <ISearch size={16} style={{ color: mutedColor, flexShrink: 0 }} />
          <input
            ref={searchRef}
            type="text"
            placeholder={`Search ${stores.length} stores...`}
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              backgroundColor: 'transparent',
              fontSize: '13px',
              color: isDark ? '#e2e8f0' : '#0f172a',
              padding: 0,
            }}
          />
          {filterValue && (
            <div
              onClick={() => setFilterValue('')}
              style={{
                cursor: 'pointer',
                fontSize: '18px',
                color: mutedColor,
                lineHeight: 1,
                fontWeight: 300,
              }}
            >
              ×
            </div>
          )}
        </div>

        {/* Category pills */}
        {storeCategories.length > 0 && (
          <div style={{
            display: 'flex',
            gap: '6px',
            overflowX: 'auto',
            scrollbarWidth: 'none',
            WebkitOverflowScrolling: 'touch',
            paddingBottom: '12px',
          }}>
            <style>{`.stores-cat-pills::-webkit-scrollbar { display: none; }`}</style>
            {[{ _id: 'any', name: 'All' }, ...storeCategories].map((cat) => {
              const active = categoryId === cat._id;
              return (
                <div
                  key={cat._id}
                  className="stores-cat-pills"
                  onClick={() => onCategoryChange(cat._id)}
                  style={{
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    transition: 'all 0.15s',
                    backgroundColor: active
                      ? (isDark ? 'rgba(99,102,241,0.2)' : '#eef2ff')
                      : (isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'),
                    color: active
                      ? (isDark ? '#a5b4fc' : '#4f46e5')
                      : (isDark ? '#94a3b8' : '#64748b'),
                    border: active
                      ? `1px solid ${isDark ? 'rgba(99,102,241,0.3)' : '#c7d2fe'}`
                      : `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
                  }}
                >
                  {cat.name}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Store list */}
      <DialogContent style={{ padding: '0 20px 20px' }}>
        {_stores.length === 0 ? renderEmpty() : (
          <div>
            {_stores.map(renderStoreCard)}
          </div>
        )}

        {/* Quick actions bar at the bottom */}
        <div style={{
          display: 'flex',
          gap: '8px',
          padding: '12px 0 4px',
          borderTop: `1px solid ${borderColor}`,
          marginTop: '4px',
        }}>
          <div
            onClick={() => setOpenAddStoreDialog(true)}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              padding: '10px',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500,
              color: isDark ? '#a5b4fc' : '#4f46e5',
              backgroundColor: isDark ? 'rgba(99,102,241,0.1)' : '#eef2ff',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDark ? 'rgba(99,102,241,0.15)' : '#e0e7ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = isDark ? 'rgba(99,102,241,0.1)' : '#eef2ff';
            }}
          >
            <IAdd size={15} />
            <span>Add Store</span>
          </div>
          <div
            onClick={() => setOpenArchivedStoresDialog(true)}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              padding: '10px',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500,
              color: mutedColor,
              backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc',
              border: `1px solid ${borderColor}`,
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = hoverBg;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc';
            }}
          >
            <IArchive size={14} />
            <span>Archived{archivedStoresCount > 0 ? ` (${archivedStoresCount})` : ''}</span>
          </div>
        </div>
      </DialogContent>

      {/* Context menu portal */}
      {renderContextMenu()}

      {/* Sub-dialogs */}
      {openAddStoreDialog && (
        <AddStoreDialog
          open={openAddStoreDialog}
          onAdd={refreshStores}
          onClose={() => setOpenAddStoreDialog(false)}
        />
      )}
      {openEditStoreDialog && (
        <EditStoreDialog
          open={openEditStoreDialog}
          onEdit={refreshStores}
          store={selectedStore}
          onClose={() => setOpenEditStoreDialog(false)}
        />
      )}
      {openAuditDialog && (
        <AuditStoreDialog
          store={selectedStore}
          open={openAuditDialog}
          onClose={() => setOpenAuditDialog(false)}
        />
      )}
      {openArchivedStoresDialog && (
        <ArchivedStoreDialog
          onChange={() => _refresh()}
          open={openArchivedStoresDialog}
          onClose={() => setOpenArchivedStoresDialog(false)}
        />
      )}
      {openWarningsDialog && (
        <StoreArchivesWarningsDialog
          archiveWarnings={archiveWarnings}
          open={openWarningsDialog}
          onClose={() => setOpenWarningsDialog(false)}
        />
      )}
      {switchingStore && <Loading type={'serverCall'} delay={1 * 3000} />}
    </Dialog>
  );
}

StoresDialog.defaultProps = {
  onStoreSelect: () => {},
  refreshStores: () => {},
  stores: [],
};
StoresDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  stores: PropTypes.array,
  onStoreSelect: PropTypes.func,
  refreshStores: PropTypes.func,
  selectedStore: PropTypes.object,
  permissions: PropTypes.object,
};

export default StoresDialog;
